import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import CurrentDateDisplay from "./scheduleComponents/currentDateDisplay";
import CurrentTime from "./scheduleComponents/currentTime";
import DateNavigator from "./scheduleComponents/dateNavigator";
import DurationSelector from "./scheduleComponents/durationSelector";
import { fetchCliniciansByClinicId, fetchClinicians } from "../../store/thunks/cliniciansThunk";
import {
  createAppointment,
  fetchAppointmentsByDate,
  fetchAppointmentById,
} from "../../store/thunks/appointmentsThunk";
import { fetchMeetingsByDate } from "../../store/thunks/meetingsThunk";
import ClinicianSchedule from "./scheduleComponents/clinicianSchedule";
import { Switch } from "@headlessui/react";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import MultiSelect from "../../applicationUi/components/MultiSelect";
import SingleSelect from "../../applicationUi/components/SingleSelect";
import { fetchNotes } from "../../store/thunks/notesThunk";
import {
  clearAppointmentUpdated,
  clearSelectedAppointment,
  setSelectedAppointment,
} from "../../store/slices/appointmentsSlice";
import { clearCurrentInvoice } from "../../store/slices/invoiceSlice";
import {
  setSelectedClinic,
  clearSelectedClinic,
} from "../../store/slices/clinicSlice";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import AppointmentHoverTooltip from "../../applicationUi/components/AppointmentHoverTooltip";
import { resetMeetingUpdated } from "../../store/slices/meetingSlice";
import {
  clearAppointmentPayments,
  clearInvoicePayments,
} from "../../store/slices/paymentsSlice";
import { 
  XMarkIcon, 
  ChevronUpIcon, 
  ChevronDownIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarIcon
} from "@heroicons/react/24/outline";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ClinicScheduleModal({
  setModalOpen,
  onTimeSlotSelect,
  selectedClient,
  selectedService,
  selectedProduct,
  selectedClinician,
  rescheduleData, // Ensure this prop is being passed correctly
}) {
  const dispatch = useDispatch();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDurationSelector, setShowDurationSelector] = useState(false);
  const [selectorPosition, setSelectorPosition] = useState({ x: 0, y: 0 });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [showWorkingHours, setShowWorkingHours] = useState(true);
  const [workingHoursOnly, setWorkingHoursOnly] = useState(true);
  const [isWideSidePanelOpen, setIsWideSidePanelOpen] = useState(false);
  const [rescheduleDataState, setRescheduleDataState] =
    useState(rescheduleData);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showSLP, setShowSLP] = useState(false);
  const [showCDA, setShowCDA] = useState(false);
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const container = useRef(null);
  const [isHeaderMinimized, setIsHeaderMinimized] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const scrollContainerRef = useRef(null);
  const [hoveredAppointment, setHoveredAppointment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const tooltipTimeout = useRef(null);

  const appointments = useSelector(
    (state) => state.appointments.scheduleAppointments || []
  );
  const user = useSelector((state) => state.auth.user || []);
  const clinics = useSelector((state) => state.clinics.clinics || []);
  const clinicians = useSelector((state) => state.clinicians.clinicians || []);
  const notes = useSelector((state) => state.notes.notes || []);
  const meetings = useSelector((state) => state.meetings.meetings || []);
  const selectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );
  const selectedClinic = useSelector((state) => state.clinics.selectedClinic);
  const appointmentUpdated = useSelector(
    (state) => state.appointments.appointmentUpdated
  );
  const meetingUpdated = useSelector((state) => state.meetings.meetingUpdated);
  const [hideCancelled, setHideCancelled] = useState(true);

  useEffect(() => {
    dispatch(fetchClinics());
  }, [dispatch]);

  // Auto-select current user if they are a clinician
  useEffect(() => {
    if (user && user.role === "clinician" && clinicians.length > 0) {
      const currentClinician = clinicians.find(
        (clinician) => clinician.email === user.email
      );
      if (currentClinician && selectedClinicians.length === 0) {
        console.log(`[ClinicScheduleModal] Auto-selecting current clinician: ${currentClinician.firstName} ${currentClinician.lastName}`);
        setSelectedClinicians([currentClinician]);
      }
    }
  }, [user, clinicians, selectedClinicians.length]);

  useEffect(() => {
    if (selectedClinic) {
      if (selectedClinic._id === "all") {
        // Fetch all clinicians from all clinics
        dispatch(fetchClinicians());
        dispatch(
          fetchAppointmentsByDate({
            date: currentDate,
          })
        );
        dispatch(
          fetchMeetingsByDate({ date: currentDate })
        );
      } else {
        dispatch(fetchCliniciansByClinicId(selectedClinic._id));
        dispatch(
          fetchAppointmentsByDate({
            date: currentDate,
            clinicId: selectedClinic._id,
          })
        );
        dispatch(
          fetchMeetingsByDate({ date: currentDate, clinicId: selectedClinic._id })
        );
      }
      dispatch(fetchNotes(currentDate));
      dispatch(clearAppointmentUpdated());
      dispatch(resetMeetingUpdated());
    }
  }, [
    dispatch,
    selectedClinic,
    currentDate,
    appointmentUpdated,
    meetingUpdated,
    isWideSidePanelOpen,
  ]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeout.current) {
        clearTimeout(tooltipTimeout.current);
      }
    };
  }, []);

  const handleNoteUpdate = useCallback(() => {
    dispatch(fetchNotes(currentDate));
  }, [dispatch, currentDate]);

  const handleNoteDelete = useCallback(() => {
    dispatch(fetchNotes(currentDate));
  }, [dispatch, currentDate]);

  const getClinicianDailyNote = useCallback(
    (clinicianId) => {
      if (!Array.isArray(notes) || notes.length === 0) {
        return ""; // Return empty string if notes is not an array or has no notes
      }

      const clinicianNote = notes.find(
        (item) => item.clinicianId === clinicianId
      );
      // Return the note or an empty string
      return clinicianNote ? clinicianNote.notes[0] : "";
    },
    [notes]
  );

  const handleSelectClinician = (selectedClinicianIds) => {
    setSelectedClinicians(selectedClinicianIds);
  };

  const onPrevDay = () => {
    setCurrentDate(
      (prevDate) =>
        new Date(
          prevDate.getFullYear(),
          prevDate.getMonth(),
          prevDate.getDate() - 1
        )
    );
  };

  const onToday = () => {
    setCurrentDate(new Date());
  };

  const onNextDay = () => {
    setCurrentDate(
      (prevDate) =>
        new Date(
          prevDate.getFullYear(),
          prevDate.getMonth(),
          prevDate.getDate() + 1
        )
    );
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  const onPrevWeek = () => {
    setCurrentDate(
      (prevDate) =>
        new Date(
          prevDate.getFullYear(),
          prevDate.getMonth(),
          prevDate.getDate() - 7
        )
    );
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  const onNextWeek = () => {
    setCurrentDate(
      (prevDate) =>
        new Date(
          prevDate.getFullYear(),
          prevDate.getMonth(),
          prevDate.getDate() + 7
        )
    );
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  const onDateChange = (date) => {
    setCurrentDate(date);
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  // EXISTING AND FUNCTIONAL HANDLE DURATION SELECT
  const handleDurationSelect = (duration) => {
    setShowDurationSelector(false);
    const title = rescheduleDataState
      ? rescheduleDataState.title
      : prompt("Enter the appointment title:");
    const patientName = rescheduleDataState
      ? `${rescheduleDataState.client.firstName} ${rescheduleDataState.client.lastName}`
      : prompt("Enter the patient's name:");
    if (title && patientName) {
      const startTime = selectedTimeSlot;
      const endTime = calculateEndTime(startTime, parseInt(duration));
      const newAppointment = {
        ...rescheduleDataState,
        clinic: selectedClinician.clinic,
        clinician: selectedClinician._id,
        client: selectedClient._id,
        title,
        start: new Date(
          `${currentDate.toDateString()} ${startTime}`
        ).toISOString(),
        end: new Date(`${currentDate.toDateString()} ${endTime}`).toISOString(),
        service: selectedService._id,
        status: ["Booked"],
        history: [
          {
            status: "Booked",
            comments: `Created for ${patientName}`,
            user: user._id,
          },
          ...(rescheduleDataState
            ? [
                {
                  status: "Changed",
                  comments: "Rescheduled",
                  timestamp: new Date().toISOString(),
                  user: user._id,
                },
              ]
            : []),
        ],
      };
      console.log(
        "[CLINICSCHEDULEMODAL] - New Appointment Created:",
        newAppointment
      );
      dispatch(createAppointment(newAppointment)).then(() => {
        // refetchAppointments();
      });
    }
    setRescheduleDataState(null);
  };

  const calculateEndTime = (startTime, duration) => {
    const [hour, minute] = startTime.split(":").map(Number);
    const time = new Date();
    time.setHours(hour, minute + duration, 0);
    return `${time.getHours()}:${time
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  };

  const handleCancel = () => {
    setShowDurationSelector(false);
  };

  const handleAppointmentHover = async (appointment, event) => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const scrollContainer = scrollContainerRef.current;
    const scrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;

    setTooltipPosition({
      x: rect.right + 10 - scrollLeft,
      y: rect.top + rect.height / 2,
    });

    // Fetch full appointment data including history
    try {
      const fullAppointment = await dispatch(fetchAppointmentById(appointment._id)).unwrap();
      setHoveredAppointment(fullAppointment);
    } catch (error) {
      console.error('[Clinic Schedule] Failed to fetch appointment details:', error);
      // Fallback to showing appointment without history
      setHoveredAppointment(appointment);
    }

    tooltipTimeout.current = setTimeout(() => {
      setShowTooltip(true);
    }, 300);
  };

  const handleAppointmentHoverEnd = () => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
    
    tooltipTimeout.current = setTimeout(() => {
      // Only hide if tooltip itself is not hovered
      if (!isTooltipHovered) {
        setShowTooltip(false);
        setHoveredAppointment(null);
      }
    }, 150);
  };

  const handleTooltipHover = () => {
    setIsTooltipHovered(true);
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
  };

  const handleTooltipHoverEnd = () => {
    setIsTooltipHovered(false);
    
    tooltipTimeout.current = setTimeout(() => {
      setShowTooltip(false);
      setHoveredAppointment(null);
    }, 150);
  };

  const handleMeetingUpdate = () => {
    if (selectedClinic._id === "all") {
      dispatch(fetchMeetingsByDate({ date: currentDate }));
    } else {
      dispatch(
        fetchMeetingsByDate({ date: currentDate, clinicId: selectedClinic._id })
      );
    }
  };

  const handleAppointmentClick = (appointment) => {
    // console.log("Appointment before dispatch:", appointment); // Validate here
    dispatch(setSelectedAppointment(appointment));
    setIsWideSidePanelOpen(true);
  };

  const handleCloseWideSidePanel = () => {
    setIsWideSidePanelOpen(false);
    // console.log("WideSidePanel closed");
  };

  const handleReschedule = (appointment) => {
    console.log(
      "[CLINICSCHEDULEMODAL] - handleReschedule called with appointment:",
      appointment
    );
    setRescheduleDataState(appointment);
  };

  const filteredClinicians = useMemo(() => {
    const cliniciansToFilter =
      selectedClinicians.length > 0 ? selectedClinicians : clinicians;
    return cliniciansToFilter
      .filter((clinician) => {
        const nameMatch = `${clinician.firstName} ${clinician.lastName}`
          .toLowerCase()
          .includes(filterText.toLowerCase());
        const specializationMatch =
          (!showSLP && !showCDA) ||
          (showSLP && clinician.specialization.includes("SLP")) ||
          (showCDA && clinician.specialization.includes("CDA"));
        // Exclude if userStatus is not "active"
        return (
          nameMatch && specializationMatch && clinician.userStatus === "active"
        );
      })
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
  }, [selectedClinicians, clinicians, filterText, showSLP, showCDA]);

  const filteredAppointments = useMemo(() => {
    return (appointments || []).filter((appointment) => {
      if (!appointment || !Array.isArray(appointment.status)) {
        console.warn(
          "[ClinicScheduleModal] Skipping invalid appointment:",
          appointment
        );
        return false; // Skip invalid appointments
      }

      if (hideCancelled) {
        return !appointment.status.some((status) =>
          [
            "Cancelled",
            "Cancelled - No Fee",
            "Cancelled - Charge Fee",
            "Cancelled - Clinician",
            "Cancelled - Client",
            "No Show",
            "No Show - No Fee",
            "No Show - Charge Fee",
          ].includes(status)
        );
      }

      return true;
    });
  }, [appointments, hideCancelled]);

  const handleScroll = (event) => {
    setScrollLeft(event.target.scrollLeft);
    
    // Handle vertical scroll for header minimization
    const currentScrollTop = event.target.scrollTop;
    const scrollThreshold = 50; // Minimum scroll distance before triggering
    
    if (currentScrollTop > lastScrollTop && currentScrollTop > scrollThreshold) {
      // Scrolling down
      setIsHeaderMinimized(true);
    } else if (currentScrollTop < lastScrollTop && currentScrollTop < scrollThreshold) {
      // Scrolling up or near top
      setIsHeaderMinimized(false);
    }
    
    setLastScrollTop(currentScrollTop);
  };

  const handleClinicChange = (clinicId) => {
    if (clinicId === "all") {
      // Create a special "all clinics" object
      const allClinicsOption = { _id: "all", name: "All Clinics" };
      dispatch(setSelectedClinic(allClinicsOption));
      
      // Fetch appointments for all clinics (don't pass clinicId)
      dispatch(
        fetchAppointmentsByDate({
          date: currentDate,
        })
      );
    } else {
      const clinic = clinics.find((c) => c._id === clinicId);
      if (!clinic) {
        console.error(`[ClinicScheduleModal] Selected clinic not found in the list of clinics.`);
        return;
      }
      dispatch(setSelectedClinic(clinic));

      dispatch(
        fetchAppointmentsByDate({
          date: currentDate,
          clinicId: clinic._id,
        })
      );
    }
  };

  return (
    <div className="flex h-full flex-col bg-gray-50/50">
      {showDurationSelector && (
        <DurationSelector
          x={selectorPosition.x}
          y={selectorPosition.y}
          onSelect={handleDurationSelect}
          onCancel={handleCancel}
        />
      )}
      
      {/* Collapsible Header */}
      <header className={`sticky top-0 z-50 bg-white border-b shadow-sm transition-all duration-300 ease-in-out ${
        isHeaderMinimized ? 'h-16' : 'h-auto'
      }`}>
        {/* Full Header */}
        <div className={`transition-all duration-300 ease-in-out ${
          isHeaderMinimized ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100 h-auto'
        }`}>
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-6">
              <div className="flex flex-col gap-3">
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  <SingleSelect
                    options={[
                      { value: "all", label: "All Clinics" },
                      ...clinics.map((clinic) => ({
                        value: clinic._id,
                        label: clinic.name,
                      }))
                    ]}
                    selectedValue={
                      selectedClinic
                        ? selectedClinic._id === "all"
                          ? { value: "all", label: "All Clinics" }
                          : { value: selectedClinic._id, label: selectedClinic.name }
                        : null
                    }
                    onChange={(selected) => {
                      if (selected) {
                        handleClinicChange(selected.value);
                      }
                    }}
                    placeholder="Select a clinic..."
                    className="w-[280px]"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                  <MultiSelect
                    options={clinicians
                      .filter((clinician) => clinician.userStatus === "active")
                      .map((clinician) => ({
                        value: clinician._id,
                        label: `${clinician.firstName} ${clinician.lastName}`,
                      }))}
                    selectedValues={selectedClinicians.map((clinician) => ({
                      value: clinician._id,
                      label: `${clinician.firstName} ${clinician.lastName}`,
                    }))}
                    onChange={(selectedList) => {
                      const selectedClinicianObjects = selectedList
                        .map((item) =>
                          clinicians.find((clinician) => clinician._id === item.value)
                        )
                        .filter(Boolean);
                      handleSelectClinician(selectedClinicianObjects);
                    }}
                    placeholder="Select clinicians..."
                    className="w-[280px]"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={hideCancelled}
                        onChange={() => setHideCancelled(!hideCancelled)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <span className="text-sm text-gray-700">Hide Cancelled & No Shows</span>
                    </label>
                    <label className="flex items-center space-x-2 mt-2">
                      <input
                        type="checkbox"
                        checked={workingHoursOnly}
                        onChange={() => setWorkingHoursOnly(!workingHoursOnly)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <span className="text-sm text-gray-700">Working Hours</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={showSLP}
                        onChange={() => setShowSLP(!showSLP)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <span className="text-sm text-gray-700">SLP only</span>
                    </label>
                    <label className="flex items-center space-x-2 mt-2">
                      <input
                        type="checkbox"
                        checked={showCDA}
                        onChange={() => setShowCDA(!showCDA)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <span className="text-sm text-gray-700">CDA only</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-gray-600">
                <ClockIcon className="h-4 w-4" />
                <CurrentTime />
              </div>
              <div className="flex items-center space-x-4">
                <CurrentDateDisplay currentDate={currentDate} />
                <DateNavigator
                  onPrevDay={onPrevDay}
                  onToday={onToday}
                  onNextDay={onNextDay}
                  onPrevWeek={onPrevWeek}
                  onNextWeek={onNextWeek}
                  onDateChange={onDateChange}
                />
              </div>
              <button
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                onClick={() => setModalOpen(false)}>
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Minimized Header */}
        <div className={`transition-all duration-300 ease-in-out ${
          isHeaderMinimized ? 'opacity-100 h-16' : 'opacity-0 h-0 overflow-hidden'
        }`}>
          <div className="flex items-center justify-between px-6 h-full">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  {selectedClinic?._id === 'all' ? 'All Clinics' : selectedClinic?.name || 'Select Clinic'}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <UserGroupIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {selectedClinicians.length > 0 
                    ? `${selectedClinicians.length} clinician${selectedClinicians.length > 1 ? 's' : ''} selected`
                    : 'All clinicians'
                  }
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-gray-600">
                <ClockIcon className="h-4 w-4" />
                <CurrentTime />
              </div>
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                <CurrentDateDisplay currentDate={currentDate} />
                <DateNavigator
                  onPrevDay={onPrevDay}
                  onToday={onToday}
                  onNextDay={onNextDay}
                  onPrevWeek={onPrevWeek}
                  onNextWeek={onNextWeek}
                  onDateChange={onDateChange}
                />
              </div>
              <button
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                onClick={() => setModalOpen(false)}>
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 transition-opacity duration-300 ${
          isHeaderMinimized ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="flex items-center space-x-1 text-xs text-gray-400 bg-white px-2 py-1 rounded-t-lg border border-gray-200">
            <ChevronUpIcon className="h-3 w-3" />
            <span>Scroll up to expand</span>
          </div>
        </div>
      </header>

      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-auto" 
        onScroll={handleScroll}
      >
        <div
          className="isolate flex flex-auto bg-white"
          style={{ minWidth: `${filteredClinicians.length * 320}px` }}>
          {filteredClinicians.map((clinician, index) => (
            <div
              ref={container}
              className="flex flex-auto flex-col min-w-80 border-r border-gray-200 last:border-r-0"
              key={index}>
              <ClinicianSchedule
                clinician={clinician}
                appointments={filteredAppointments}
                selectedClient={selectedClient}
                selectedService={selectedService}
                selectedClinician={selectedClinician}
                currentDate={currentDate}
                handleAppointmentClick={handleAppointmentClick}
                showWorkingHours={showWorkingHours}
                onReschedule={handleReschedule}
                rescheduleData={rescheduleData}
                scrollLeft={scrollLeft}
                clinic={selectedClinic}
                clinicWorkingHours={selectedClinic?.workingHours}
                dailyNote={getClinicianDailyNote(clinician._id)}
                handleNoteUpdate={handleNoteUpdate}
                handleNoteDelete={handleNoteDelete}
                meetings={meetings.filter((meeting) =>
                  meeting.attendees.some(
                    (attendee) => attendee._id === clinician._id
                  )
                )}
                handleMeetingUpdate={handleMeetingUpdate}
                onAppointmentHover={handleAppointmentHover}
                onAppointmentHoverEnd={handleAppointmentHoverEnd}
                showAllClinics={selectedClinic?._id === "all"}
                clinicianClinic={clinician.clinic}
                allClinics={clinics}
              />
            </div>
          ))}
        </div>
      </div>

      {selectedAppointment && (
        <AppointmentSummary
          title={"Appointment Summary"}
          open={isWideSidePanelOpen}
          onClose={handleCloseWideSidePanel}
          onReschedule={handleReschedule}
        />
      )}

      <AppointmentHoverTooltip
        appointment={hoveredAppointment}
        position={tooltipPosition}
        isVisible={showTooltip}
        onTooltipHover={handleTooltipHover}
        onTooltipHoverEnd={handleTooltipHoverEnd}
      />
    </div>
  );
}